// based on
// https://github.com/the-road-to-react-with-firebase/react-redux-firebase-authentication/blob/master/src/components/Firebase/firebase.js

import firebase from 'firebase/app/dist/index.esm.js';
import 'firebase/firestore/dist/index.esm.js';
import 'firebase/auth/dist/index.esm.js';
import 'firebase/messaging/dist/index.esm.js';
import superagent from 'superagent';
import store from '../../store/index.js';

import config from '../../client-config.js';
import { setOrUpdateOrgId } from 'actions/session.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import { unsetMasqueradeMode } from 'actions/ui.js';
import { clearLocalFeatures } from 'contexts/FeatureContext.jsx';
import customAuth from 'services/auth/index.js';

firebase.initializeApp(config);

if (config.firestore) {
  const firestore = firebase.firestore();
  firestore.settings(config.firestore);
}

class Firebase {
  constructor() {
    // this.serverValue = app.database.ServerValue;
    this.emailAuthProvider = firebase.auth.EmailAuthProvider;
    this.isSignedInListeners = new Set();
    this.isSignedOutListeners = new Set();
    this.authUser = null;
    this.authenticatedAs = null;
    this.initAuthListener();

    this.microsoftProvider = new firebase.auth.OAuthProvider('microsoft.com');
    this.microsoftProvider.setCustomParameters({
      // Force re-consent.
      prompt: 'select_account',
    });
    this.microsoftProvider.addScope('User.Read');
    this.microsoftProvider.addScope('openid');
    this.microsoftProvider.addScope('profile');
  }

  get firebase() {
    return firebase;
  }

  get auth() {
    const auth = firebase.auth();
    if (process.env.FIREBASE_AUTH_EMULATOR_HOST) {
      auth.useEmulator(`http://${process.env.FIREBASE_AUTH_EMULATOR_HOST}`);
    }
    return auth;
  }

  get firestore() {
    return firebase.firestore();
  }

  get messaging() {
    return firebase.messaging();
  }

  get fieldValue() {
    return firebase.firestore.FieldValue;
  }

  get timestamp() {
    return firebase.firestore.Timestamp;
  }

  setPendingCredential = (provider, email, credential) => {
    this.pendingCredential = { provider, email, credential };
  }

  getIdToken = async () => {
    return await this.auth?.currentUser?.getIdToken();
  };

  getAgent = async () => {
    const idToken = await this.getIdToken();
    const fnRegion = config.region ? config.region : 'us-central1';
    const clientAPIBaseUrl = config.clientAPIBaseUrl || `https://${encodeURIComponent(fnRegion)}-${encodeURIComponent(config.projectId)}.cloudfunctions.net/api`;
    return ['get', 'post', 'put', 'patch', 'delete'].reduce((a, verb) => {
      a[verb] = (...args) => {
        args = args.slice(0);
        args[0] = `${clientAPIBaseUrl}${args[0]}`;
        const agent = superagent[verb](...args)
          .set('Accept', 'application/json');
        if (idToken) {
          agent.set('Authorization', `Bearer ${idToken}`);
        }
        return agent;
      };
      return a;
    }, {});
  };

  createAuthCode = async () => {
    const clientAuthBaseUrl = config.clientAuthBaseUrl || `https://us-central1-${config.projectId}.cloudfunctions.net/auth`;
    const idToken = await this.auth.currentUser.getIdToken(); // firebase token
    return superagent.post(`${clientAuthBaseUrl}/signin/authcode`)
      .set('Accept', 'application/json')
      .set('Authorization', `Bearer ${idToken}`)
      .send({}).then(result => result.body.authCode);
  };

  // *** Auth API ***
  doCreateUserWithEmailAndPassword = (email, password) => this.auth.createUserWithEmailAndPassword(email, password);

  doSignInWithEmailAndPassword = async (email, password) => {
    // Remove ssoSessionId from local storage, if it would have been
    // left over from a previous SSO session
    localStorage.removeItem('ssoSessionId');

    let token;
    try {
      const clientAuthBaseUrl = config.clientAuthBaseUrl || `https://us-central1-${config.projectId}.cloudfunctions.net/auth`;
      const response = await superagent
        .post(`${clientAuthBaseUrl}/signin/password`)
        .send({ email, password });
      token = response.body.token;
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 400) {
        err.code = 'auth/wrong-password'; // Mimic authenitcation error code as using client side firebase auth
      }
      throw err;
    }

    if (!token) {
      throw new Error('Token not generated?');
    }

    // As signin happens on backend we'll need to signin with the provided custom token on client side
    return this.auth.signInWithCustomToken(token);
  };

  doSendSignInWithEmailLink = (email, next) => {
    const encodedEmail = encodeURIComponent(email);
    const nextLink = !!next ? `&next=${next}` : '';
    const url = `${window.location.origin}/signin?email=${encodedEmail}${nextLink}`;
    const clientAuthBaseUrl = config.clientAuthBaseUrl || `https://us-central1-${config.projectId}.cloudfunctions.net/auth`;

    return superagent.post(`${clientAuthBaseUrl}/signin/email`).send({
      url,
      email,
    });
  };

  isSignInWithEmailLink = url => this.auth.isSignInWithEmailLink(url);

  doSignInWithEmailLink = (email, link) => {
    // Remove ssoSessionId from local storage, if it would have been
    // left over from a previous SSO session
    localStorage.removeItem('ssoSessionId');
    return this.auth.signInWithEmailLink(email, link);
  };

  doSignInWithCustomToken = (token) => this.auth.signInWithCustomToken(token);

  doSignInWithGoogle = () => this.auth.signInWithPopup(this.googleProvider);

  doSignInWithFacebook = () => this.auth.signInWithPopup(this.facebookProvider);

  doSignInWithTwitter = () => this.auth.signInWithPopup(this.twitterProvider);

  doSignInWithMicrosoft = () => this.auth.signInWithPopup(this.microsoftProvider).then(async result => {
    // At this point firebase have verified the token for us, we know this is a user who signed in with microsoft and is a valid user for that tenant ID
    // given result.additionalUserInfo.profile or result.user or decodedToken
    const response = await this.syncMicrosoftAccessRights(result.credential.idToken, result.user.email);
    // Store returned ssoSessionId in local storage, so we can access it at SSO logout
    const { body: { ssoSessionId } } = response;
    localStorage.setItem('ssoSessionId', ssoSessionId);
    return response;
  });

  syncMicrosoftAccessRights = async (token, email = '') => {
    const clientAuthBaseUrl = config.clientAuthBaseUrl || `https://us-central1-${config.projectId}.cloudfunctions.net/auth`;
    const idToken = await this.auth.currentUser.getIdToken(); // firebase token

    return superagent
      .post(`${clientAuthBaseUrl}/signin/sso`)
      .set('Accept', 'application/json')
      .set('Authorization', `Bearer ${idToken}`)
      .send({
        provider: 'microsoft',
        idToken: token, // microsoft token
      })
      .catch((e) => {
        this.doSignOut();
        throw {
          status: e.status,
          message: 'Failed to login with Microsoft',
          email,
        };
      });
  };

  doSignOut = async () => {
    const ssoSessionId = localStorage.getItem('ssoSessionId');
    if (ssoSessionId) {
      await this.doSSOSignOut(ssoSessionId);
      return;
    }
    this.doLocalSignout();
  }

  doLocalSignout = () => {
    clearLocalFeatures();
    this.auth.signOut();
    trackingService.track({ eventType: 'sign-out' });
  }

  doSSOSignOut = async (ssoSessionId) => {
    // Handle sso signout flow; if sso logout is enabled for user, redirect to sso logout
    // endpoint in addition to local signout
    const clientAuthBaseUrl = config.clientAuthBaseUrl;
    const redirectUri = config.returnUrl;
    const idToken = await this.auth.currentUser.getIdToken();
    let url, ssoLogout;

    try {
      const response = await superagent
        .post(`${clientAuthBaseUrl}/signout/sso`)
        .set('Accept', 'application/json')
        .set('Authorization', `Bearer ${idToken}`)
        .send({
          provider: 'microsoft',
          ssoSessionId,
          redirectUri,
        });

      ({ body: { url = '', ssoLogout } } = response);
    } catch (e) {
      console.error('Failed to logout from SSO provider', e);
    } finally {
      localStorage.removeItem('ssoSessionId');
      // Ensure we always sign out locally for all sso users
      this.doLocalSignout();
      if (ssoLogout && url) {
        // Finally, redirect user to SSO logout url is sso logout is enabled
        window.location.href = url;
      }
    }
  }

  doPasswordReset = email => {
    const encodedEmail = encodeURIComponent(email);
    const clientAuthBaseUrl = config.clientAuthBaseUrl || `https://us-central1-${config.projectId}.cloudfunctions.net/auth`;

    return superagent.post(`${clientAuthBaseUrl}/resetPassword/email/${encodedEmail}`).send();
  };

  doSendEmailVerification = async () => {
    const actionCodeSettings = {
      url: config.returnUrl || `https://${config.authDomain}`,
    };

    if (config.useCustomAuth) {
      const idToken = await this.auth.currentUser.getIdToken();
      await customAuth.sendEmailVerification(idToken, actionCodeSettings);
    } else {
      await this.auth.currentUser.sendEmailVerification(actionCodeSettings);
    }
  }

  doPasswordUpdate = password => this.auth.currentUser.updatePassword(password);

  // *** Merge Auth and DB User API *** //

  onAuthUserListener = (onSignedIn, onSignedOut) => {
    const _onSignedIn = authUser => onSignedIn && onSignedIn(authUser);
    const _onSignedOut = () => onSignedOut && onSignedOut();
    this.isSignedInListeners.add(_onSignedIn);
    this.isSignedOutListeners.add(_onSignedOut);
    if (this.authUser === null) {
      // Auth state not yet determined, just wait
    } else {
      // Replay last auth state
      this.authUser ? _onSignedIn(this.authUser) : _onSignedOut();
    }
    return () => {
      this.isSignedInListeners.delete(_onSignedIn);
      this.isSignedOutListeners.delete(_onSignedOut);
    };
  };

  getIdentityProviderData = () => {
    if (config.useCustomAuth) {
      return this.authUser.providerData;
    }
    return this.auth.currentUser?.providerData || [];
  }

  unlinkMicrosoftAccount = async () => {
    if (!config.useCustomAuth) {
      await this.auth.currentUser.unlink('microsoft.com');
    } else {
      const idToken = await this.auth.currentUser.getIdToken();
      await customAuth.unlinkMicrosoftAccount(idToken);
      this.authUser = await customAuth.getAuthUser(idToken);
    }
  };

  linkMicrosoftAccount = async () => {
    if (!config.useCustomAuth) {
      await this.auth.currentUser.linkWithPopup(this.microsoftProvider);
    } else {
      const idToken = await this.auth.currentUser.getIdToken();
      await customAuth.linkMicrosoftInPopup(idToken);
      this.authUser = await customAuth.getAuthUser(idToken);
    }
  };

  maybeLinkCredential = (authUser) => {
    if (this.pendingCredential && this.pendingCredential.email === authUser.email) {
      if (this.pendingCredential.provider === 'microsoft') {
        try {
          authUser.linkWithCredential(this.pendingCredential.credential);
          // TODO: Could also do the post to signin/sso here to sync access rights
          // according to AD directly instead of needing another logout and login
        } catch (e) {
          console.error('Failed to link to microsoft', e.message);
        }
      }
    }
    this.pendingCredential = null;
  }

  initAuthListener = () => {
    const isSignedIn = (authUser) => {
      this.authUser = authUser;
      this.isSignedInListeners.forEach(callback => callback(authUser));
    };
    const isSignedOut = () => {
      this.authUser = undefined;
      this.isSignedOutListeners.forEach(callback => callback());
      trackingService.clearUserData(() =>
        trackingService.track({ eventType: 'de-identify' }, { GA: true }));
    };

    this.auth.onAuthStateChanged(async tokenUser => {
      if (!tokenUser) {
        this.pendingCredential = null;

        const { ui: { masqueradeMode } } = store.getState();

        if (!!masqueradeMode) {
          trackingService.track({
            eventType: 'masquerade:exit',
          });
          store.dispatch(unsetMasqueradeMode());
        }

        if (this.userDocListener) this.userDocListener(); // unsubscribe
        if (this.membershipsListener) this.membershipsListener(); // unsubscribe
        this.authenticatedAs = null;
        return isSignedOut();
      }
      this.authenticatedAs = tokenUser.uid;

      const creationTime = this.auth.currentUser?.metadata?.creationTime;

      let authUser;
      if (config.useCustomAuth) {
        const idToken = await this.auth.currentUser.getIdToken();
        authUser = await customAuth.getAuthUser(idToken);
      } else {
        authUser = tokenUser;
      }

      this.maybeLinkCredential(authUser);

      const { firestore } = this;

      if (this.userDocListener) this.userDocListener(); // unsubscribe
      this.userDocListener = await firestore
        .collection('users').doc(authUser.uid)
        .onSnapshot(userDoc => {
          if (this.membershipsListener) this.membershipsListener(); // unsubscribe

          if (!userDoc.exists || !userDoc.data()) {
            console.log('no user data');
            return isSignedOut();
          }
          const userData = userDoc.data();

          store.dispatch(setOrUpdateOrgId(userData.orgId));

          this.membershipsListener = firestore
            .collectionGroup('users')
            .where('uid', '==', authUser.uid)
            .onSnapshot(async membershipsSnapshot => {
              if (membershipsSnapshot.metadata.fromCache) return;
              const memberships = (await Promise.all(membershipsSnapshot.docs
                // Ignore docs without membership properties
                .filter(orgUser => orgUser.data().uid && orgUser.data().orgId)
                // Only use org membership docs, ignore /users for example
                .filter(orgUser => orgUser.ref.path === `organizations/${orgUser.data().orgId}/users/${orgUser.data().uid}`)
                // Look up org info
                .map(async orgUser => ({
                  org: await firestore.collection('organizations').doc(orgUser.data().orgId).get(),
                  user: orgUser.data(),
                }))))
                .filter(orgUser => orgUser.org.exists) // TODO: show error
                .map(orgUser => ({ ...orgUser, org: orgUser.org.data() }));

              const user = {
                ...(userData.isSuperadmin ? { isSuperadmin: true, canPromoteToSuperadmin: !!userData.canPromoteToSuperadmin } : {}),
                uid: authUser.uid,
                email: authUser.email,
                emailVerified: authUser.emailVerified,
                providerData: authUser.providerData,
                displayName: userData.displayName || '',
                creationTime,
                memberships,
                membership: (orgId) => memberships
                  .filter(m => m.user.orgId === orgId)[0],
                hasOrgRole: (orgId, role) => memberships
                  .filter(m => m.user.orgId === orgId)
                  .filter(m => m.user.roles.includes(role))
                  .length > 0,
              };

              if (user.uid === this.authenticatedAs) {
                isSignedIn(user);
              } else {
                console.log('Ignoring outdated authUser update');
              }
            });
        });
    });
  };
}

export default Firebase;
